/**
 * Search's loading shapes — one composite `pendingComponent` plus the
 * per-section skeletons it, and the sections' own `Suspense` fallbacks, render.
 *
 * `Label` renders for real throughout: it's static chrome, no data behind it.
 */
import { ChevronLeft, Loader2, Search } from 'lucide-react'

import { AIRailLayout } from '@/features/ai/components/panel/ai-rail-layout'
import { Section } from '@/features/app-shell/section'
import { useSearchPageContext } from '@/features/search/ai/hooks/use-search-page-context'
import { SearchPageHeader } from '@/features/search/components/page-header'
import { SearchFilters } from '@/features/search/components/search-filters'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * The saved-search trigger's footprint, with a spinner for the icon.
 *
 * Lives here, not beside the control: two fallbacks must look identical — the
 * lazy chunk's `Suspense` in `input/index.tsx` and the list query's `Suspense`
 * in `input/saved-searches.tsx` — and a static import of the latter from the
 * former would pull the lazy chunk back into the eager bundle (P-21).
 */
export const SavedSearchesSkeleton = () => (
	<Button type="button" variant="outline" size="icon" disabled>
		<Loader2 className="size-4 animate-spin" />
	</Button>
)

/**
 * Mirrors `IntegrationSelect` — the Log Source + Event Type field pair.
 */
export const IntegrationSelectSkeleton = () => {
	return (
		<>
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<Label>Log Source</Label>
				<Skeleton className="h-10 w-full" />
			</div>
			<div className="flex min-w-0 flex-1 flex-col gap-2">
				<Label>Event Type</Label>
				<Skeleton className="h-10 w-full" />
			</div>
		</>
	)
}

/**
 * Mirrors `TimeRange` — the timeframe trigger + presets button.
 */
export const TimeRangeSkeleton = () => {
	return (
		<div className="flex min-w-0 flex-1 flex-col gap-2">
			<Label>Timeframe</Label>

			<div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
				<Skeleton className="size-10 shrink-0" />
				<Skeleton className="h-10 min-w-0 flex-1" />
			</div>
		</div>
	)
}

/**
 * Mirrors `QueryBuilderForm` with an empty query: the root rule group is 50px
 * — a 1px border, 8px padding and the 32px header row (measured). A query
 * with rules is taller; the skeleton cannot know how many there are.
 */
export const QueryBuilderSkeleton = () => (
	<Skeleton className="h-[50px] w-full rounded-lg" />
)

/**
 * Mirrors `SearchInput` — the saved-search trigger, the query builder and the
 * submit button, in the form's own row classes. Submit is static chrome, so it
 * renders for real, disabled.
 */
export const QueryInputSkeleton = () => (
	<div className="flex w-full flex-col items-start justify-start gap-2 sm:flex-row">
		<SavedSearchesSkeleton />
		<QueryBuilderSkeleton />
		<Button type="button" size="icon" variant="outline" disabled>
			<Search className="size-4" />
		</Button>
	</div>
)

/**
 * The field rows `FieldList` shows while its schemas load. Its lazy chunk's
 * fallback, `FieldListSkeleton`, shows the same rows.
 */
export const FieldRowsSkeleton = () => (
	<div className="flex flex-col gap-2">
		<Skeleton className="h-6 w-full" />
		<Skeleton className="h-6 w-full" />
		<Skeleton className="h-6 w-full" />
	</div>
)

/**
 * Mirrors `FieldList` while its chunk loads: the real search input, disabled,
 * above the rows.
 */
export const FieldListSkeleton = () => (
	<div className="flex flex-col gap-2">
		<Input type="text" placeholder="Search fields…" disabled />
		<FieldRowsSkeleton />
	</div>
)

/**
 * Mirrors `SearchSidebar` expanded, its classes copied from `sidebar/index.tsx`
 * — the component is lazy, so importing it here would pull its chunk into the
 * eager bundle (P-21). The collapse toggle renders disabled. Below the
 * auto-collapse width the real sidebar opens collapsed, and this one does not.
 */
export const SearchSidebarSkeleton = () => (
	<div className="relative hidden h-fit w-72 shrink-0 flex-col gap-4 sm:flex">
		<div className="bg-background sticky top-0 z-10 flex w-full flex-col border-b px-2 pb-4 shadow-md">
			<div className="flex flex-row items-center justify-between">
				<p className="shrink-0 font-bold">Field Names</p>
				<Button variant="ghost" size="icon" className="mt-2 p-0" disabled>
					<ChevronLeft className="size-4" />
				</Button>
			</div>
		</div>

		<FieldListSkeleton />
	</div>
)

/**
 * Mirrors `ResultCount` — the centred "N Results" line and the searched window.
 */
export const ResultCountSkeleton = () => (
	<div className="flex w-full flex-col items-center justify-center gap-1">
		<Skeleton className="h-7 w-32" />
		<Skeleton className="h-5 w-64" />
	</div>
)

/**
 * Mirrors `ResultChart` — the 200px histogram, hidden below `sm` like the
 * chart itself.
 */
export const ResultChartSkeleton = () => (
	<Skeleton className="hidden h-[200px] w-full sm:block" />
)

const RESULT_ROW_COUNT = 6

/**
 * Mirrors `SearchResultList` — the bordered list of collapsed result cards,
 * with the same row shape the list renders while it loads the next page.
 */
export const ResultListSkeleton = () => (
	<div className="w-full rounded-md border">
		{Array.from({ length: RESULT_ROW_COUNT }, (_, index) => (
			<div
				key={index}
				className="flex h-14 items-center gap-3 border-b p-3 last:border-b-0"
			>
				<Skeleton className="size-4 shrink-0" />
				<Skeleton className="h-4 w-32 shrink-0" />
				<div className="flex flex-1 gap-2">
					<Skeleton className="h-5 w-20" />
					<Skeleton className="h-5 w-24" />
					<Skeleton className="h-5 w-16" />
					<Skeleton className="h-5 w-28" />
				</div>
				<Skeleton className="h-4 w-16 shrink-0" />
			</div>
		))}
	</div>
)

/**
 * Mirrors `SearchResult` — the results card with its three sections. Shown
 * while `int` waits for its default sources; each section's `Suspense` uses
 * the matching part above, so the card keeps its shape when they mount.
 */
export const SearchResultsSkeleton = () => (
	<div
		className="bg-card flex w-full flex-col gap-4 rounded-md border p-4"
		data-testid="search-results-pending"
		aria-busy
	>
		<ResultCountSkeleton />
		<ResultChartSkeleton />
		<ResultListSkeleton />
	</div>
)

/**
 * The route's `pendingComponent`: the page's own layout with the data taken
 * out. Each region is the export its section's `Suspense` also falls back to,
 * so the two cannot drift. The header and filter badges read no server data
 * and render for real. Ask Noah is left out: see `SearchPageHeader`.
 *
 * `AIRailLayout` wraps this because it mirrors the real page's outermost
 * element: the rail is static chrome with no server dependency, so leaving it
 * out here would make it pop in and re-flow the content column once the route
 * resolves. `useSearchPageContext` reads URL search params only, so it resolves
 * during the pending state without suspending. The real page also passes a
 * `ref` to `<Section>` for container-width measurement — the skeleton has no
 * width-dependent child, so it deliberately doesn't.
 */
export const SearchSkeleton = () => {
	const pageContext = useSearchPageContext()

	return (
		<AIRailLayout pageContext={pageContext}>
			<Section>
				<SearchPageHeader />

				<div className="flex w-full flex-col items-start justify-start gap-4">
					<div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row">
						<IntegrationSelectSkeleton />
						<TimeRangeSkeleton />
					</div>

					<QueryInputSkeleton />

					<SearchFilters />

					<div className="flex w-full flex-row items-start justify-start gap-6">
						<SearchSidebarSkeleton />
						<SearchResultsSkeleton />
					</div>
				</div>
			</Section>
		</AIRailLayout>
	)
}
